'use client';

import { useState, useEffect } from 'react';
import { markMessageAsRead, deleteMessage, updateMessageStatus } from "@/lib/actions/messages";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Mail, MailOpen, Trash2, Eye, Calendar, User, CheckCircle2, Clock, CheckCheck } from "lucide-react";
import { format } from "date-fns";

interface MessageListProps {
  initialMessages: any[];
}

export function MessageList({ initialMessages }: MessageListProps) {
  const [messages, setMessages] = useState(initialMessages);
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleView = async (message: any) => {
    setSelectedMessage(message);
    setIsViewOpen(true);
    
    if (message.status === 'NEW') {
      const result = await markMessageAsRead(message.id);
      if (result.success) {
        setMessages(prev => 
          prev.map(m => m.id === message.id ? { ...m, isRead: true, status: 'READ' } : m)
        );
      }
    }
  };

  const handleToggleFollowUp = async (e: React.MouseEvent, message: any) => {
    e.stopPropagation();
    const newStatus = message.status === 'FOLLOWED_UP' ? 'READ' : 'FOLLOWED_UP';
    
    const result = await updateMessageStatus(message.id, newStatus);
    if (result.success) {
      const updatedMessages = messages.map(m => 
        m.id === message.id ? { ...m, status: newStatus } : m
      );
      setMessages(updatedMessages);
      
      if (selectedMessage?.id === message.id) {
        setSelectedMessage({ ...selectedMessage, status: newStatus });
      }
      
      toast.success(newStatus === 'FOLLOWED_UP' ? "Marked as Followed Up" : "Status updated");
    } else {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this message?")) return;

    const result = await deleteMessage(id);
    if (result.success) {
      setMessages(prev => prev.filter(m => m.id !== id));
      if (selectedMessage?.id === id) setIsViewOpen(false);
      toast.success("Message deleted");
    } else {
      toast.error("Failed to delete message");
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'FOLLOWED_UP':
        return (
          <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100 py-0.5 px-2">
            <span className="flex items-center gap-1.5"><CheckCheck size={12} /> FOLLOWED UP</span>
          </Badge>
        );
      case 'READ':
        return (
          <Badge variant="outline" className="text-gray-500 border-gray-200 bg-gray-50/50 py-0.5 px-2">
            <span className="flex items-center gap-1.5"><CheckCircle2 size={12} /> READ</span>
          </Badge>
        );
      default:
        return (
          <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 py-0.5 px-2">
            <span className="flex items-center gap-1.5"><Clock size={12} /> NEW</span>
          </Badge>
        );
    }
  };

  return (
    <>
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-gray-50/50">
            <TableRow>
              <TableHead className="w-[50px]"></TableHead>
              <TableHead>Sender</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {messages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-20 text-gray-500">
                  <div className="flex flex-col items-center gap-3">
                    <div className="bg-gray-100 p-4 rounded-full">
                      <Mail size={40} className="text-gray-300" />
                    </div>
                    <p className="text-lg font-medium text-gray-400">No messages found</p>
                    <p className="text-sm">Inquiries from your contact form will appear here.</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              messages.map((msg) => (
                <TableRow 
                  key={msg.id} 
                  className={`group cursor-pointer transition-colors ${msg.status === 'FOLLOWED_UP' ? 'opacity-60 bg-gray-50/30' : msg.status === 'READ' ? 'opacity-85 hover:bg-gray-50' : 'bg-blue-50/20 hover:bg-blue-50/40'}`}
                  onClick={() => handleView(msg)}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {msg.status === 'FOLLOWED_UP' ? (
                      <div className="p-2 bg-green-100 rounded-lg w-fit text-green-600">
                        <CheckCheck size={16} />
                      </div>
                    ) : msg.status === 'READ' ? (
                      <div className="p-2 bg-gray-100 rounded-lg w-fit text-gray-400">
                        <MailOpen size={16} />
                      </div>
                    ) : (
                      <div className="p-2 bg-primary/10 rounded-lg w-fit text-primary">
                        <Mail size={16} />
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className={`text-sm ${msg.status === 'NEW' ? 'font-bold text-navy' : 'font-medium text-gray-700'}`}>
                        {msg.name}
                      </span>
                      <span className="text-xs text-gray-400 font-normal">{msg.email}</span>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[250px]">
                    <div className="truncate text-sm text-navy">
                      {msg.subject}
                    </div>
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(msg.status)}
                  </TableCell>
                  <TableCell className="text-xs text-gray-400 whitespace-nowrap">
                    {mounted ? format(new Date(msg.createdAt), "MMM dd, yyyy · HH:mm") : "---"}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={(e) => handleToggleFollowUp(e, msg)}
                        title={msg.status === 'FOLLOWED_UP' ? "Mark as Read" : "Mark as Followed Up"}
                        className={msg.status === 'FOLLOWED_UP' ? "text-green-600 hover:bg-green-50" : "text-gray-400 hover:text-green-600 hover:bg-green-50"}
                      >
                        <CheckCheck className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleView(msg)} className="hover:bg-blue-50 hover:text-blue-600 text-gray-400">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(msg.id)} className="hover:bg-red-50 hover:text-red-600 text-gray-400">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden border-0 shadow-2xl rounded-2xl [&>button]:hidden">
          {selectedMessage && (
            <div className="flex flex-col h-full bg-white">
              {/* Header */}
              <div className={`p-6 text-white transition-colors duration-300 ${selectedMessage.status === 'FOLLOWED_UP' ? 'bg-gradient-to-r from-green-600 to-green-700' : 'bg-gradient-to-r from-navy to-[#2A3B61]'}`}>
                <div className="flex justify-between items-start mb-4">
                  <div className="p-2 bg-white/10 rounded-xl backdrop-blur-sm">
                    {selectedMessage.status === 'FOLLOWED_UP' ? <CheckCheck className="h-6 w-6" /> : <MailOpen className="h-6 w-6 text-primary" />}
                  </div>
                  <div className="flex gap-2">
                    {selectedMessage.status === 'FOLLOWED_UP' ? (
                      <Badge variant="outline" className="text-white border-white/20 bg-white/10 backdrop-blur-sm py-1 px-3">
                         FOLLOWED UP
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-white border-white/20 bg-white/5 backdrop-blur-sm py-1 px-3">
                        {selectedMessage.status === 'READ' ? 'READ' : 'NEW'}
                      </Badge>
                    )}
                  </div>
                </div>
                <DialogTitle className="text-2xl font-bold leading-tight mb-2">
                  {selectedMessage.subject}
                </DialogTitle>
                <div className="flex items-center gap-4 text-sm text-gray-200">
                  <span className="flex items-center gap-1.5 font-medium text-white">
                    <User size={14} className={selectedMessage.status === 'FOLLOWED_UP' ? "text-white" : "text-primary"} /> {selectedMessage.name}
                  </span>
                  <span className="opacity-40">|</span>
                  <span className="flex items-center gap-1.5">
                    <Calendar size={14} /> {mounted ? format(new Date(selectedMessage.createdAt), "PPPP 'at' p") : "---"}
                  </span>
                </div>
              </div>

              {/* Sender Details Bar */}
              <div className="px-6 py-4 bg-gray-50 border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Sender Email:</div>
                  <div className="text-sm font-semibold text-navy bg-white border px-3 py-1 rounded-full shadow-sm">
                    {selectedMessage.email}
                  </div>
                </div>
                <Button 
                  onClick={(e) => handleToggleFollowUp(e, selectedMessage)}
                  variant={selectedMessage.status === 'FOLLOWED_UP' ? "default" : "outline"}
                  size="sm"
                  className={`gap-1.5 rounded-full px-4 h-8 text-xs font-bold transition-all ${selectedMessage.status === 'FOLLOWED_UP' ? 'bg-green-600 hover:bg-green-700 text-white border-0' : 'border-green-200 text-green-700 hover:bg-green-50'}`}
                >
                  <CheckCheck size={14} />
                  {selectedMessage.status === 'FOLLOWED_UP' ? 'Followed Up' : 'Mark as Followed Up'}
                </Button>
              </div>

              {/* Message Content */}
              <div className="p-8 min-h-[250px]">
                <p className="text-gray-400 text-[10px] font-black uppercase tracking-[0.2em] mb-4">Message Content</p>
                <div className="text-navy text-base leading-relaxed whitespace-pre-wrap">
                  {selectedMessage.message}
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 bg-gray-50 border-t flex justify-between items-center">
                <Button 
                  variant="ghost" 
                  onClick={() => handleDelete(selectedMessage.id)}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50 gap-2 h-10 px-4 rounded-xl font-medium"
                >
                  <Trash2 size={16} /> Delete
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setIsViewOpen(false)} className="rounded-xl px-6 h-10 font-bold border-gray-300">
                    Back to List
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
